import { ErrorBoundary } from '@sentry/react';
import { formatDistanceToNowShort } from '@/utilities/date';
import { BreadcrumbNav } from './components/BreadcrumbNav';
import { CompanyHeader } from './components/CompanyHeader';
import { ActionButtonGroup } from './components/ActionButtonGroup';
import { PriceAnalyticsCard } from './components/PriceAnalyticsCard';
import { PortfolioPositionCard } from './components/PortfolioPositionCard';
import { BlurredBackground } from './components/BlurredBackground';
import { DataSources } from './components/DataSources';
import { Bones } from './components/Bones';
import { useMetricData } from './hooks/useMetricData';
import { useLocale } from '@/hooks/useLocale';

// For FloatingMenu so it can get the intersection point
export const HEADER_BOTTOM = 'header-bottom';

export const Header = () => {
  const data = useMetricData();
  const countryISO = useLocale();

  const updated =
    data.status === 'loading' ? (
      <Bones className="w-[20ch]" />
    ) : (
      <time dateTime={data.lastAnalysisUpdateFormatted}>
        {formatDistanceToNowShort(data.lastAnalysisUpdateFormatted, {
          countryISO,
        })}
      </time>
    );

  return (
    <header className="relative z-1 overflow-hidden [--bar-h:5rem] [-webkit-tap-highlight-color:transparent] light:max-lg:mb-1 lg:mb-x2">
      <div className="relative light:bg-p-white max-md:pb-x2 md:bg-[white]/[.01] lg:rounded-md lg:shadow-lg">
        <BlurredBackground />
        <div className="relative grid gap-x1_5 px-x2 pt-x2 md:grid-cols-[1.618fr_minmax(310px,1fr)] md:gap-x-x4 md:gap-y-x2 md:px-x4">
          <section className="grid gap-inherit self-center">
            <BreadcrumbNav>
              <span className="text-tiny md:hidden">{updated}</span>
            </BreadcrumbNav>
            <div>
              <CompanyHeader />
            </div>
            <div className="grid max-sm:gap-inherit">
              <div className="my-x0_5 empty:hidden md:mb-0">
                <ErrorBoundary
                  fallback={
                    <div className="text-xs text-bad">
                      {/* TODO: i18n */}
                      Something went wrong, please refresh the page.
                    </div>
                  }
                >
                  <ActionButtonGroup />
                </ErrorBoundary>
              </div>
              <div className="hidden min-h-[--bar-h] flex-wrap content-center items-center gap-x2 text-tiny md:flex md:gap-y-0">
                <div className="whitespace-nowrap text-tiny text-softer">
                  {updated}
                </div>
                <div className="whitespace-nowrap">
                  <DataSources />
                </div>
              </div>
            </div>
          </section>
          <div className="grid auto-rows-[2.75fr_1fr] gap-x0_75 lg:auto-rows-[2.5fr_1fr]">
            <section className="relative self-center">
              <PriceAnalyticsCard />
            </section>
            <section className="h-full">
              <ErrorBoundary
                fallback={
                  <div className="text-xs text-bad">
                    {/* TODO: i18n */}
                    Something went wrong, please refresh the page.
                  </div>
                }
              >
                <PortfolioPositionCard />
              </ErrorBoundary>
            </section>
          </div>
          <div className="text-center text-tiny md:hidden">
            <DataSources />
          </div>
        </div>
      </div>

      <span id={HEADER_BOTTOM} />
    </header>
  );
};
